// Client types + helpers for Home v2 threads. Mirrors the worker route shapes
// (worker/routes/threads.ts) so the frontend doesn't reparse, plus the
// persisted-row → chat view-model mapper that lets ChatLog render history and
// the live streaming turn with one renderer.

import { authFetch } from '@/lib/api'

type GetToken = () => Promise<string | null>

export type ThreadStatus = 'open' | 'closed'

export type ThreadRailItem = {
  id: string
  customer_id: string | null
  customer_name: string | null
  title: string | null
  vehicle_label: string | null
  appointment_at: string | null
  appointment_status: string | null
  status: ThreadStatus
  is_unfiled: boolean
  last_message_at: string | null
  closed_at: string | null
  close_reason: string | null
  revenue_cad: number | null
}

export type ThreadMessageRole =
  | 'user'
  | 'eric'
  | 'tool'
  | 'confirm'
  | 'system'
  | 'report'

export type DispatchResult = { id: string; label: string; url: string }

export type ThreadMessageCard = {
  kind?: string
  tool?: string
  label?: string
  detail?: string
  mode?: string
  intent?: string
  result?: DispatchResult | null
  guardrail_decision?: string | null
  [key: string]: unknown
}

export type ThreadMessageRow = {
  id: string
  role: ThreadMessageRole
  body: string | null
  card: ThreadMessageCard | null
  assistant_request_id: string | null
  created_at: string
  undoable: boolean
  undone_at: string | null
  confidence: number | null
}

export type ThreadRecord = {
  customer: {
    id: string
    name: string | null
    phone: string | null
    email: string | null
    lifetime_value_cad: number | null
  } | null
  vehicle: {
    id: string
    label: string | null
    vin: string | null
    plate: string | null
    mileage: number | null
    color: string | null
  } | null
  appointment: {
    id: string
    starts_at: string | null
    ends_at: string | null
    status: string | null
    notes: string | null
  } | null
  set_by_chat: string[]
}

export type ThreadDetail = {
  thread: {
    id: string
    customer_id: string | null
    customer_name: string | null
    vehicle_label: string | null
    title: string | null
    status: ThreadStatus
    is_unfiled: boolean
    closed_at: string | null
    close_reason: string | null
    revenue_cad: number | null
    last_message_at: string | null
    created_at: string
  }
  messages: ThreadMessageRow[]
  record: ThreadRecord
}

export const threadKeys = {
  rail: (status: ThreadStatus | 'all' = 'open') => ['home-v2-rail', status] as const,
  detail: (id: string) => ['thread', id] as const,
  archive: (q: string) => ['threads-archive', q] as const,
}

export function fetchRail(
  getToken: GetToken,
  status: ThreadStatus | 'all' = 'open',
  q?: string,
): Promise<{ threads: ThreadRailItem[] }> {
  const qs = new URLSearchParams({ status })
  if (q && q.trim()) qs.set('q', q.trim())
  return authFetch<{ threads: ThreadRailItem[] }>(`/api/threads?${qs.toString()}`, getToken)
}

// ── Home draft persistence — the in-progress unfiled thread ──────────────────
// An unfiled thread is a freeform Eric conversation that hasn't resolved to a
// customer yet (is_unfiled true, customer_id null — anchor-thread sets both
// together when it files). Home auto-reopens the most recent one on mount so
// navigating away doesn't lose the draft; once the thread files into the rail
// (is_unfiled flips) it stops auto-reopening, and Clear (closeThread) abandons
// it — the closed draft stays recoverable in the Threads archive.

// The rail arrives ordered by recency — COALESCE(last_message_at, created_at)
// DESC in GET /threads (worker/routes/threads.ts) — so the first open unfiled
// row is the most recent in-progress draft. Null = nothing to restore (fresh
// composer).
//
// Scope note (accepted): the rail is shop-wide, so this can pick a draft
// another staff member started — unlike the server-side counterpart
// findOpenUnfiledThread (worker/lib/threads/anchor-thread.ts), which scopes
// to the requesting creator so one user's "new chat" can't swallow another's
// in-flight draft. Accepted here because the rail itself is shop-wide (any
// staff can open any thread) and reopening is navigation, not a write.
// Bound: the pick only sees the rail page the client fetched (GET /threads
// defaults to limit=20) — fine, since recency ordering puts the draft first.
export function pickAutoReopenThread(threads: ThreadRailItem[]): string | null {
  const draft = threads.find((x) => x.status === 'open' && x.is_unfiled)
  return draft?.id ?? null
}

// Clear is only offered (and only fires) for an open unfiled draft — it must
// NEVER touch a filed/customer thread (those wrap up via Close instead). The
// customer_id check is belt-and-suspenders next to is_unfiled.
export function canClearThread(
  thread:
    | Pick<ThreadRailItem, 'status' | 'is_unfiled' | 'customer_id'>
    | null
    | undefined,
): boolean {
  return (
    thread != null &&
    thread.status === 'open' &&
    thread.is_unfiled &&
    thread.customer_id == null
  )
}

// Open (or reuse) the caller's unfiled "New chat" thread — POST /threads
// shares anchorThread's reuse rule server-side, so calling this repeatedly
// can't mint duplicate drafts. Used by the dock voice mic, which needs a
// thread id to hand its clip to before any turn exists.
export function createDraftThread(getToken: GetToken): Promise<{ thread_id: string }> {
  return authFetch<{ thread_id: string }>('/api/threads', getToken, { method: 'POST' })
}

export function closeThread(getToken: GetToken, id: string): Promise<{ ok: boolean }> {
  return authFetch<{ ok: boolean }>(
    `/api/threads/${encodeURIComponent(id)}/close`,
    getToken,
    { method: 'POST' },
  )
}

// Synchronous rail-cache repair after a successful close. Flips the closed
// row's status in place WITHOUT removing it — the evaporate animation still
// needs the row to render, and pickAutoReopenThread / canClearThread already
// filter on status — so a navigate-away-and-back inside the 420ms evaporate
// window can't auto-reopen the cleared draft from a stale cache. Unloaded
// cache or missing row = no-op (same reference back, no re-render).
export function markThreadClosedInRail(
  data: { threads: ThreadRailItem[] } | undefined,
  id: string,
): { threads: ThreadRailItem[] } | undefined {
  if (!data) return data
  if (!data.threads.some((x) => x.id === id)) return data
  return {
    ...data,
    threads: data.threads.map((x) =>
      x.id === id ? { ...x, status: 'closed' } : x,
    ),
  }
}

// Rail rows to actually RENDER (HomeFeed chips + ThreadWorkspace switcher
// pills). The repaired cache deliberately keeps the closed row in the array
// (markThreadClosedInRail — the evaporate animation needs it), but if the
// evaporate's invalidate gets cancelled (Clear → navigate away inside the
// 420ms window → return within staleTime), that closed row would otherwise
// render as a normal tappable chip — and a composer turn on a closed thread
// files into a DIFFERENT thread (anchor-thread falls through on a closed id).
// Hide closed rows EXCEPT the one currently evaporating.
export function visibleRailThreads(
  threads: ThreadRailItem[],
  evaporatingId: string | null,
): ThreadRailItem[] {
  return threads.filter((x) => x.status !== 'closed' || x.id === evaporatingId)
}

export function reopenThread(getToken: GetToken, id: string): Promise<{ ok: boolean }> {
  return authFetch<{ ok: boolean }>(
    `/api/threads/${encodeURIComponent(id)}/reopen`,
    getToken,
    { method: 'POST' },
  )
}

export function fetchThread(getToken: GetToken, id: string): Promise<ThreadDetail> {
  return authFetch<ThreadDetail>(`/api/threads/${encodeURIComponent(id)}`, getToken)
}

// Reverse an Eric/cascade action recorded on an assistant_requests row.
export function undoEricAction(
  getToken: GetToken,
  requestId: string,
): Promise<{ ok: boolean; kind?: string }> {
  return authFetch<{ ok: boolean; kind?: string }>(
    `/api/dashboard/eric-undo/${encodeURIComponent(requestId)}`,
    getToken,
    { method: 'POST' },
  )
}

// ── Deterministic Cascade actions, scoped to a thread ────────────────────────
// These hit the same /api/dashboard/* fast-lane endpoints the home feed uses
// (no Eric brain), passing the thread_id so the worker records the action into
// the thread transcript (a `confirm` result row + Undo). The response's
// `request_id` is the Undo handle; `thread_id` is non-null when it persisted.

export type SlotOption = { starts_at_iso: string; label: string }

export function fetchAvailableSlots(
  getToken: GetToken,
  opts: { durationMin?: number; limit?: number } = {},
): Promise<{ slots: SlotOption[] }> {
  const qs = new URLSearchParams({
    duration_min: String(opts.durationMin ?? 60),
    limit: String(opts.limit ?? 6),
  })
  return authFetch<{ slots: SlotOption[] }>(
    `/api/dashboard/available-slots?${qs.toString()}`,
    getToken,
  )
}

export type BookForThreadResult = {
  ok: boolean
  id: string
  customer: { id: string; name: string }
  starts_at_iso: string
  url: string
  request_id: string | null
  thread_id: string | null
  conflicts?: { id: string; customer_name: string | null; starts_at_iso: string }[]
  carryover?: { id: string; body: string }[]
  deduped?: boolean
}

export function bookForThread(
  getToken: GetToken,
  input: {
    customerId: string
    startsAtIso: string
    slotLabel: string
    notes: string | null
    threadId: string
  },
): Promise<BookForThreadResult> {
  return authFetch<BookForThreadResult>('/api/dashboard/book', getToken, {
    method: 'POST',
    body: JSON.stringify({
      customer_id: input.customerId,
      starts_at_iso: input.startsAtIso,
      slot_label: input.slotLabel,
      notes: input.notes,
      thread_id: input.threadId,
    }),
  })
}

export type NoteTarget = 'vehicle' | 'customer'

export type CascadeFieldResult = {
  ok: boolean
  kind: NoteTarget
  id: string
  label: string
  url: string
  request_id: string | null
  thread_id: string | null
}

// "Add note" — append to the target's notes (never replace); Undo restores the
// prior text verbatim.
export function appendNoteForThread(
  getToken: GetToken,
  input: { kind: NoteTarget; id: string; value: string; threadId: string },
): Promise<CascadeFieldResult> {
  return authFetch<CascadeFieldResult>('/api/dashboard/update-field', getToken, {
    method: 'PATCH',
    body: JSON.stringify({
      kind: input.kind,
      id: input.id,
      field: 'notes',
      value: input.value,
      append: true,
      thread_id: input.threadId,
    }),
  })
}

export type AddVehicleResult = {
  ok: boolean
  vehicle: { id: string; label: string; url: string }
  // null on the orphan create path (no owner). The thread-cascade caller always
  // supplies a customerId so it gets a non-null customer back, but the endpoint
  // now also serves ownerless creates — keep the shared type honest.
  customer: { id: string; name: string } | null
  request_id: string | null
  thread_id: string | null
}

// On a duplicate, the worker returns 409 → authFetch throws an AuthFetchError
// whose `.body` is `{ error:'vehicle_exists', existing:{id,label,url} }`. Callers
// catch it to offer "already exists — open it" (no transcript row is written).
export function addVehicleForThread(
  getToken: GetToken,
  input: {
    customerId: string
    year: number | null
    make: string | null
    model: string | null
    vin: string | null
    threadId: string
  },
): Promise<AddVehicleResult> {
  return authFetch<AddVehicleResult>(
    '/api/dashboard/add-vehicle-for-customer',
    getToken,
    {
      method: 'PATCH',
      body: JSON.stringify({
        customer_id: input.customerId,
        year: input.year,
        make: input.make,
        model: input.model,
        vin: input.vin,
        thread_id: input.threadId,
      }),
    },
  )
}

// ── Chat view-model — the single shape ChatLog renders, produced by BOTH the
// persisted rows (below) and the live streaming turn (ThreadChat). ───────────

// A voice-originated user turn. `audioUrl` is a same-session object URL (the
// local recording — no fetch, no auth) or the worker's authed proxy path
// (`/api/eric/voice/:id/audio`) on a reloaded row; VoiceBubble tells them apart
// by the `blob:` prefix. `transcribing` marks the live gap before the
// transcript exists (the bubble shows its named step instead of a toggle).
export type UserVoiceVM = {
  voiceMessageId: string | null
  durationMs: number
  audioUrl: string
  transcribing?: boolean
}

export type ChatVM =
  // imageUrl: a live object-URL thumbnail for a photo attached this session.
  // hasImage: a reloaded turn that carried a photo (breadcrumb only — bytes
  // aren't persisted), so a caption-less photo turn shows a "[photo]" tag
  // instead of an empty bubble.
  // voice: the turn originated as a voice message — `text` is then the
  // transcript, rendered behind the bubble's Transcript toggle.
  | {
      kind: 'user'
      id: string
      text: string
      imageUrl?: string
      hasImage?: boolean
      voice?: UserVoiceVM
    }
  | { kind: 'eric'; id: string; text: string; streaming?: boolean }
  | { kind: 'tool'; id: string; tool: string; detail?: string | null }
  | {
      kind: 'result'
      id: string
      result: DispatchResult
      intent: string
      requestId: string | null
      undoable: boolean
      undoneAt: string | null
      createdAt: string
    }
  | { kind: 'system'; id: string; text: string }
  // A `report:` problem report Emil filed (NOT sent to Eric). `text` is the
  // elaboration; rendered as a calm "Reported to debug log" card.
  | { kind: 'report'; id: string; text: string }

// One persisted thread_messages row → a chat VM (null = nothing to render, e.g.
// a 'confirm' row whose card carried no result).
export function rowToVM(row: ThreadMessageRow): ChatVM | null {
  switch (row.role) {
    case 'user': {
      // Voice-originated turn: the card carries the replay id + duration
      // (persisted by persistDispatchTurn's user_voice card); body = transcript.
      if (row.card?.kind === 'user_voice' && typeof row.card.voice_message_id === 'string') {
        // Coerce defensively — JSONB round-trips can stringify numbers.
        const rawDuration = Number(row.card.duration_ms)
        const durationMs = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 0
        return {
          kind: 'user',
          id: row.id,
          text: row.body ?? '',
          voice: {
            voiceMessageId: row.card.voice_message_id,
            durationMs,
            audioUrl: `/api/eric/voice/${row.card.voice_message_id}/audio`,
          },
        }
      }
      return {
        kind: 'user',
        id: row.id,
        text: row.body ?? '',
        hasImage: row.card?.kind === 'user_image',
      }
    }
    case 'eric':
      return { kind: 'eric', id: row.id, text: row.body ?? '' }
    case 'tool':
      return {
        kind: 'tool',
        id: row.id,
        tool: (row.card?.tool as string) ?? 'tool',
        detail: row.card?.detail ?? null,
      }
    case 'system':
      return { kind: 'system', id: row.id, text: row.body ?? '' }
    case 'report':
      return { kind: 'report', id: row.id, text: row.body ?? '' }
    case 'confirm': {
      const result = row.card?.result
      if (!result) return null
      return {
        kind: 'result',
        id: row.id,
        result,
        intent: (row.card?.intent as string) ?? '',
        requestId: row.assistant_request_id,
        undoable: row.undoable,
        undoneAt: row.undone_at,
        createdAt: row.created_at,
      }
    }
    default:
      return null
  }
}

// Outer guardrail for showing the transcript's Undo button — NOT a countdown
// (none is shown). The server decides real undoability on click (reversibility
// state), so this just keeps an ancient turn from showing a dead Undo. Was 5
// minutes; widened to 24h so a still-reversible action isn't hidden prematurely.
export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000

export function withinUndoWindow(createdAtIso: string): boolean {
  const t = new Date(createdAtIso).getTime()
  return Number.isFinite(t) && Date.now() - t < UNDO_WINDOW_MS
}

// Tool-chip detail line — the entity a chip acted on, sourced from the turn's
// focus. MIRRORS `toolCardDetail` in worker/lib/threads/anchor-thread.ts so the
// live chip (derived here at onDone from the done envelope's focus) reads
// identically to the reloaded one (persisted server-side from the same focus —
// it's the only per-turn entity data both the SSE stream and the persist path
// see). Keep the two tool groups in sync.
const CUSTOMER_TOOLS = new Set([
  'lookup_customer',
  'create_customer',
  'update_customer',
  'tag_customer',
])
const VEHICLE_TOOLS = new Set([
  'lookup_vehicle',
  'create_vehicle',
  'update_vehicle',
  'decode_vin',
  'update_vehicle_notes',
  'tag_vehicle',
])
export function toolChipDetail(
  tool: string,
  focus:
    | { customerName: string | null; vehicleLabel?: string | null }
    | null
    | undefined,
): string | null {
  if (!focus) return null
  if (CUSTOMER_TOOLS.has(tool)) return focus.customerName ?? null
  if (VEHICLE_TOOLS.has(tool)) return focus.vehicleLabel ?? null
  return null
}
